/**
 * This is a generic dialog that can be used to capture a user's email address and
 * name. It is used to capture user emails for update notifications.
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Alert, Box, Button, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import BaseDataEntryDialog from "./BaseDataEntryDialog.jsx";
import { joinEmailList } from "store/reducers/inputReducer.js";
import DialogTextField from "components/common/inputs/DialogTextField.js";
import { track, EVENTS } from "utils/analytics.js";
import { getAttributionSourceSuffix } from "utils/attribution.js";

const DEFAULT_SUBMIT_BUTTON_TITLE = "Submit";

function CaptureEmailDialog(props) {
  const {
    open,
    handleClose,
    title,
    content,
    source,
    submitButtonTitle,
    useOrganization = false,
    useMessage = false,
  } = props;

  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [organization, setOrganization] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [emailTouched, setEmailTouched] = useState(false);

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

  const clearForm = () => {
    setEmail("");
    setName("");
    setOrganization("");
    setMessage("");
    setEmailTouched(false);
  };

  const submitForm = async () => {
    // Send everything the form collects. organization/message were being
    // dropped here even though the thunk, the request helper and the endpoint
    // all accept them — so every "Contact Sales" enquiry arrived as a bare
    // email address with no indication of what the person actually wanted.
    // The source carries a compact first-touch ad-attribution suffix so each
    // EMAIL_LIST row records which campaign produced the lead.
    const suffix = getAttributionSourceSuffix();
    setSubmitting(true);
    setSubmitError(null);
    try {
      // `.unwrap()` is the whole point of this function. Without it the dispatch
      // is fire-and-forget: the thunk can reject, the address can go nowhere, and
      // the user still sees "submitted successfully". That is exactly what
      // happened on dev — the dialog reported success while nothing was sent.
      await dispatch(joinEmailList({
        email,
        name,
        source: suffix ? `${source} | ${suffix}` : source,
        organization,
        message,
      })).unwrap();
    } catch (error) {
      // Keep the form populated so the address does not have to be retyped.
      // The thunk's reason is internal ("Exception: …"); show something a person
      // can act on and leave the detail to the console.
      console.error("joinEmailList failed", error);
      setSubmitError(
        "We couldn't submit that just now. Please check your connection and try again.",
      );
      return;
    } finally {
      setSubmitting(false);
    }
    // Only past this point did anything actually happen. LEAD_CAPTURED used to
    // fire regardless, so analytics counted leads that were never stored.
    track(EVENTS.LEAD_CAPTURED, { source });
    clearForm();
    setSubmitted(true);
  };

  const handleDialogClose = () => {
    setSubmitted(false);
    setSubmitError(null);
    clearForm();
    handleClose();
  };

  const emailError = emailTouched && !isValidEmail(email);
  const isFormValid = name.trim() && isValidEmail(email);

  return (
    <BaseDataEntryDialog
      open={open}
      onClose={handleDialogClose}
      title={title}
      description={content}
      maxWidth="sm"
      titleId="capture-email-dialog-title"
      descriptionId="capture-email-dialog-description"
      actions={
        !submitted ? (
          <>
            <Button
              onClick={handleDialogClose}
              variant="outlined"
              disabled={submitting}
              sx={{
                borderColor: "primary.main",
                color: "primary.main",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "primary.extraDark",
                  backgroundColor: "primary.extraLight",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={submitForm}
              variant="contained"
              disabled={!isFormValid || submitting}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                px: 2.25,
              }}
            >
              {submitting
                ? "Sending…"
                : submitButtonTitle || DEFAULT_SUBMIT_BUTTON_TITLE}
            </Button>
          </>
        ) : (
          <Button
            onClick={handleDialogClose}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              px: 2.25,
            }}
          >
            Close
          </Button>
        )
      }
    >
      {!submitted ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {submitError && (
            // role="alert" so the failure is announced, not just drawn — this
            // dialog is reachable from the a11y suite and a silent error state
            // is the same bug one layer up.
            <Alert severity="error" role="alert" onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}
          <DialogTextField
            label="Your name"
            placeholder="Enter your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            size="small"
          />

          {useOrganization && (
            <DialogTextField
              label="Organization"
              placeholder="Your organization"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              fullWidth
              size="small"
            />
          )}

          <DialogTextField
            label="Email address"
            placeholder="your@email.com"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
            fullWidth
            required
            size="small"
            error={emailError}
            // Was a hard-coded " " to hold the line open. That reserved a blank
            // row under one field and not the others, which is what made the
            // spacing look arbitrary — the gap after the email field was larger
            // than every other gap, permanently.
            helperText={emailError ? "Please enter a valid email address" : undefined}
          />

          {useMessage && (
            <DialogTextField
              label="Additional message (optional)"
              placeholder="Any additional information..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            py: 3,
          }}
        >
          <CheckCircleOutlineIcon
            sx={{ fontSize: 64, color: "success.main", mb: 2 }}
          />
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "dark.main", mb: 1 }}
          >
            Thank you!
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Your information has been submitted successfully.
          </Typography>
        </Box>
      )}
    </BaseDataEntryDialog>
  );
}

CaptureEmailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  source: PropTypes.string.isRequired,
  submitButtonTitle: PropTypes.string,
  useOrganization: PropTypes.bool,
  useMessage: PropTypes.bool,
};

export default CaptureEmailDialog;
